import { ogImageMeta } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import logo from "@/assets/thevibingco_logo.svg.asset.json";
import { MagneticButton } from "@/components/magnetic-button";
import { TalkToJonButton } from "@/components/talk-to-jon";
import { SiteFooter } from "@/components/site-footer";
import { PricingPlans } from "@/components/pricing-plans";

export const FAQS = [
  {
    q: "What does a Lovable developer actually do?",
    a: "We design, build, secure and ship apps inside Lovable — and we know the parts Lovable doesn't do well yet (migrations, complex auth, accessibility, performance). We pair with you in your own workspace, or we take the whole build off your hands.",
  },
  {
    q: "Can I hire a Lovable expert for one week?",
    a: "Yes. Most engagements start as a fixed-scope week: we land in your project, ship the most painful thing, and you decide if you want us to stay. No retainers, no minimums.",
  },
  {
    q: "Do you work inside our Lovable workspace or in your own?",
    a: "Both. We can join your workspace as a collaborator, or build a clean version in ours and hand the keys over once it ships.",
  },
  {
    q: "What's the difference between hiring you and hiring a regular dev shop?",
    a: "We live in Lovable, Cursor, Claude Code and Codex every day. A regular shop will rebuild your prompt-built app in React from scratch, charge you for three months, and lose your speed. We keep the vibe-coded speed and add the engineering rigor underneath.",
  },
];

export const Route = createFileRoute("/services/lovable-development")({
  head: () => ({
    meta: [
      { title: "Product Design & Development · Lovable Experts | Vibing Co" },
      {
        name: "description",
        content:
          "Product design and development as one team. Full-stack designers and vetted Lovable experts design your SaaS, build the whole stack and ship it in bi-weekly sprints. Trusted by 25+ teams.",
      },
      { property: "og:title", content: "Product design & development · Lovable experts | Vibing Company" },
      ...ogImageMeta("services-lovable-development", "Share card: product design and development with Lovable experts"),
      {
        property: "og:description",
        content:
          "One team for UI/UX and the full stack. Vetted Lovable developers design, ship and secure your product in bi-weekly sprints. Pause anytime.",
      },
      { property: "og:url", content: "https://vibing.inc/services/lovable-development" },
    ],
    links: [{ rel: "canonical", href: "https://vibing.inc/services/lovable-development" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Lovable development",
          serviceType: "Lovable development agency · hire Lovable expert",
          provider: { "@type": "Organization", name: "the Vibing Company", url: "https://vibing.inc/" },
          areaServed: "Worldwide",
          url: "https://vibing.inc/services/lovable-development",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: LovableDev,
});

function LovableDev() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" tabIndex={-1}>
      <Hero />
      <Capabilities />
      <Process />
      <PricingPlans heading="Where most teams start" />
      <FAQ />
      <CTA />
      </main>
      <SiteFooter />
    </div>
  );
}


function Hero() {
  return (
    <section aria-labelledby="ld-h" className="relative mx-auto max-w-[1400px] px-6 md:px-10 pt-[clamp(9rem,18vh,13rem)] pb-16 md:pb-24">
      <h1
        id="ld-h"
        className="mt-8 font-display tracking-[-0.035em] leading-[1.02] max-w-[18ch]"
        style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}
      >
        Product design and development, one team.
      </h1>
      <p className="mt-10 max-w-[60ch] text-xl md:text-2xl leading-snug text-foreground">
        Full-stack designers and vetted Lovable experts who own your product end to end: UI/UX, the stack underneath, security and shipping. Bi-weekly sprints, pause anytime — the speed of vibe coding without the mess.
      </p>
      <div className="mt-10 md:mt-14 flex flex-wrap gap-3">
        <TalkToJonButton variant="dark" immersive />
        <MagneticButton href="/work" variant="outline-dark">
          See our work
        </MagneticButton>
      </div>
    </section>
  );
}

const CAPS = [
  { k: "01", t: "Lovable app development", b: "Brief in, production app out. End-to-end build inside Lovable, with a real engineering review before anything ships." },
  { k: "02", t: "Rescue stalled Lovable projects", b: "You vibe-coded into a corner. We diagnose, refactor and unstick — usually inside a week." },
  { k: "03", t: "Backend, auth & cloud migration", b: "Move off Lovable Cloud onto your own Supabase. Row-level security, payments, edge functions, the lot." },
  { k: "04", t: "UI / UX design that doesn't look vibe-coded", b: "Custom design systems, motion, semantic HTML. Your app stops looking like every other Lovable build." },
];

function Capabilities() {
  return (
    <section aria-labelledby="cap-h" className="relative border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-32">
      <h2 id="cap-h" className="fluid-h2 font-display max-w-[16ch]">A Lovable team, on tap.</h2>
      <ul className="mt-14 grid md:grid-cols-2 gap-5">
        {CAPS.map((c) => (
          <li key={c.k} className="rounded-3xl border border-border bg-background p-8 md:p-12">
            <div className="flex items-baseline gap-5">
              <span className="font-display text-electric leading-none" style={{ fontSize: "clamp(2.5rem, 4vw, 4rem)" }}>{c.k}</span>
              <h3 className="font-display tracking-[-0.03em] leading-[1.02]" style={{ fontSize: "clamp(1.5rem, 2.2vw, 2.25rem)" }}>{c.t}</h3>
            </div>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-snug max-w-[44ch]">{c.b}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

const STEPS = [
  { k: "Day 1", v: "We read your Lovable project, your goals, your stuck points. You get a written plan by end of day." },
  { k: "Day 2-4", v: "We ship the most painful slice first. Live in your workspace, or in a clean fork — your call." },
  { k: "Day 5", v: "Demo. You decide if we keep going. No contract lock-in, no surprise invoices." },
];

function Process() {
  return (
    <section aria-labelledby="proc-h" className="relative border-t border-border/60 bg-secondary/40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-32 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <h2 id="proc-h" className="fluid-h2 font-display max-w-[14ch]">A week, not a quarter.</h2>
          <p className="mt-6 text-muted-foreground max-w-prose">
            Most Lovable development agencies sell you three months. We sell you five days, then five more if you want them.
          </p>
        </div>
        <ul className="md:col-span-7 md:col-start-6 border-t border-foreground/20">
          {STEPS.map((s) => (
            <li key={s.k} className="grid grid-cols-[100px_1fr] md:grid-cols-[140px_1fr] gap-6 items-baseline py-6 border-b border-foreground/20">
              <span className="font-display tracking-[-0.03em]" style={{ fontSize: "clamp(1.5rem, 2.2vw, 2.25rem)" }}>{s.k}</span>
              <span className="text-base md:text-lg leading-snug">{s.v}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function FAQ() {
  return (
    <section aria-labelledby="faq-h" className="relative border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-32">
      <h2 id="faq-h" className="fluid-h2 font-display max-w-[18ch]">Hiring a Lovable expert, answered.</h2>
      <dl className="mt-14 border-t border-border">
        {FAQS.map((f) => (
          <div key={f.q} className="grid md:grid-cols-12 gap-6 py-8 border-b border-border">
            <dt className="md:col-span-5 font-display text-xl md:text-2xl tracking-[-0.02em] leading-snug">{f.q}</dt>
            <dd className="md:col-span-6 md:col-start-7 text-base md:text-lg text-muted-foreground leading-relaxed">{f.a}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function CTA() {
  return (
    <section aria-labelledby="cta-h" className="relative border-t border-border/60 bg-foreground text-background">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-40 grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-7">
          <h2 id="cta-h" className="font-display tracking-[-0.035em] leading-[1.02]" style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}>
            Send us the link.
          </h2>
          <p className="mt-8 text-lg md:text-xl text-background/70 max-w-[52ch] leading-snug">
             Bring your Lovable URL and where you're stuck. Jon will help shape the right engagement.
          </p>
        </div>
        <div className="md:col-span-4 md:col-start-9">
          <a href="https://cal.com/vibinc/30min" target="_blank" rel="noopener noreferrer" className="block group">
            <span className="mt-3 block font-display text-electric-bright break-all" style={{ fontSize: "clamp(1.75rem, 3vw, 2.75rem)" }}>
              Talk to Jon<span aria-hidden className="inline-block ml-2 transition-transform group-hover:translate-x-2">↗</span>
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}
